import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import moment from "moment";
import {
  DataGridPremium,
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";

import magistratArchiveService, {
  EMagistratArchiveCaseState,
  EMagistratArchiveJobStatus,
  IMagistratArchiveJob,
  magistratArchiveQueryKeys,
} from "api/magistratArchiveService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import Loading from "components/Loading";
import SearchEmptyImg from "components/SearchEmptyImg";
import MagistratArchiveReportModal from "components/Settings/MagistratArchiveReportModal";
import MagistratArchiveStateChip from "components/Settings/MagistratArchiveStateChip";
import { plGridLocales } from "utils/muiGridLocales";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC, IArchiveCaseRow, Props } from "./typings";

const gridSx = {
  border: 0,
  "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
    outline: "none !important",
  },
};

const formatDate = (value?: string | null) =>
  value ? moment(value).format("DD.MM.YYYY") : "";

/** Daty zleceń BE zapisuje w UTC. */
const formatUtc = (value?: string | null) =>
  value ? moment.utc(value).local().format("DD.MM.YYYY HH:mm") : "";

const MagistratArchiveTab: FC<Props> = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: statusData, isLoading: isStatusLoading } =
    magistratArchiveService.useGetStatus();
  const status = statusData?.data;
  const enabled = !!status?.Enabled;
  const isAdmin = !!status?.IsAdmin;
  const pending = (status?.PendingJobs ?? 0) > 0;

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);
  const { data: casesData, isFetching: isCasesFetching } =
    magistratArchiveService.useGetCases(debouncedSearch, enabled);
  const { data: jobsData, isLoading: isJobsLoading } =
    magistratArchiveService.useGetJobs(enabled, pending);
  const { mutate: importCases, isLoading: isImporting } =
    magistratArchiveService.useImportCases();
  const { mutate: retryJob } = magistratArchiveService.useRetryJob();
  const { mutate: cancelJob } = magistratArchiveService.useCancelJob();

  const [selection, setSelection] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set<GridRowId>(),
  });
  const [reportJobId, setReportJobId] = useState<string | null>(null);

  // Gdy kolejka się opróżni, lista teczek pokazuje nowe stany (Zaimportowana).
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) {
      queryClient.invalidateQueries(magistratArchiveQueryKeys.cases);
      queryClient.invalidateQueries(magistratArchiveQueryKeys.jobs);
    }
    wasPending.current = pending;
  }, [pending, queryClient]);

  const rows = useMemo<IArchiveCaseRow[]>(
    () =>
      (casesData?.data?.Employees ?? []).flatMap((employee) =>
        employee.Years.flatMap((year) =>
          year.Codes.flatMap((code) =>
            code.Cases.map((c) => ({
              id: c.MagistratCaseId,
              Employee: employee.Label,
              Year: year.Year,
              Code: code.Name ? `${code.Code} ${code.Name}` : code.Code,
              Signature: c.Signature ?? "",
              Title: c.Title ?? "",
              CreationDate: c.CreationDate,
              CloseDate: c.CloseDate,
              State: c.State,
              LastJobId: c.LastJobId,
              LastJobStatus: c.LastJobStatus,
              LastJobError: c.LastJobError,
            })),
          ),
        ),
      ),
    [casesData],
  );

  const archivedIds = useMemo(
    () =>
      rows
        .filter((r) => r.State === EMagistratArchiveCaseState.Archived)
        .map((r) => r.id),
    [rows],
  );

  // Zaznaczenie „wszystkie" w v8 to model typu exclude — liczymy tylko teczki archiwalne.
  const selectedIds = useMemo(
    () =>
      selection.type === "exclude"
        ? archivedIds.filter((id) => !selection.ids.has(id))
        : archivedIds.filter((id) => selection.ids.has(id)),
    [selection, archivedIds],
  );

  const grouping = useMemo(
    () => (isAdmin ? ["Employee", "Year", "Code"] : ["Year", "Code"]),
    [isAdmin],
  );

  const refresh = () => {
    queryClient.invalidateQueries(magistratArchiveQueryKeys.status);
    queryClient.invalidateQueries(magistratArchiveQueryKeys.cases);
    queryClient.invalidateQueries(magistratArchiveQueryKeys.jobs);
  };

  const showError = (error: unknown) =>
    notification({
      type: ENotificationType.ERROR,
      title: t("settings.archive.title"),
      text: getErrorMessage(error as never) || t("settings.archive.actionFailed"),
    });

  const handleImport = () => {
    importCases(selectedIds, {
      onSuccess: ({ data }) => {
        setSelection({ type: "include", ids: new Set<GridRowId>() });
        refresh();
        notification({
          type: ENotificationType.INFO,
          title: t("settings.archive.title"),
          text: [
            t("settings.archive.queuedCount", { count: data.Jobs.length }),
            ...data.Skipped,
          ].join("\n"),
        });
      },
      onError: showError,
    });
  };

  const handleRetry = (job: IMagistratArchiveJob) =>
    retryJob(job.Id, { onSuccess: refresh, onError: showError });

  const handleCancel = (job: IMagistratArchiveJob) =>
    cancelJob(job.Id, { onSuccess: refresh, onError: showError });

  // Akcje wierszy przez ref — kolumny siatki nie przeliczają się przy każdym renderze.
  const jobActions = useRef({ retry: handleRetry, cancel: handleCancel });
  jobActions.current = { retry: handleRetry, cancel: handleCancel };

  const caseColumns = useMemo<GridColDef<IArchiveCaseRow>[]>(
    () => [
      { field: "Employee", headerName: t("settings.archive.employee"), width: 160 },
      { field: "Year", headerName: t("settings.archive.year"), width: 70, type: "number" },
      { field: "Code", headerName: t("settings.archive.jrwaCode"), width: 160 },
      { field: "Signature", headerName: t("settings.archive.signature"), minWidth: 110, flex: 1 },
      { field: "Title", headerName: t("settings.archive.caseTitle"), minWidth: 120, flex: 2 },
      {
        field: "CreationDate",
        headerName: t("settings.archive.created"),
        width: 95,
        valueFormatter: (value: string | null) => formatDate(value),
      },
      {
        field: "State",
        headerName: t("common.status"),
        width: 150,
        renderCell: (params) =>
          params.row?.id ? (
            <div className={styles.stateCell}>
              <MagistratArchiveStateChip
                state={params.row.State}
                jobStatus={params.row.LastJobStatus}
              />
              {params.row.LastJobId &&
                params.row.LastJobStatus === EMagistratArchiveJobStatus.Failed && (
                  <HollowButton
                    text={t("settings.archive.report")}
                    onClick={() => setReportJobId(params.row.LastJobId!)}
                  />
                )}
            </div>
          ) : null,
      },
    ],
    [t],
  );

  const jobs = jobsData?.data?.Jobs ?? [];

  const jobColumns = useMemo<GridColDef<IMagistratArchiveJob>[]>(
    () => [
      { field: "CaseSignature", headerName: t("settings.archive.signature"), minWidth: 110, flex: 1 },
      { field: "CaseTitle", headerName: t("settings.archive.caseTitle"), minWidth: 120, flex: 2 },
      ...(isAdmin
        ? [
            {
              field: "ForUserLabel",
              headerName: t("settings.archive.employee"),
              width: 120,
            } as GridColDef<IMagistratArchiveJob>,
          ]
        : []),
      {
        field: "Status",
        headerName: t("common.status"),
        width: 125,
        renderCell: (params) => <MagistratArchiveStateChip jobStatus={params.row.Status} />,
      },
      {
        field: "CreationDate",
        headerName: t("settings.archive.requested"),
        width: 120,
        valueFormatter: (value: string) => formatUtc(value),
      },
      {
        field: "FinishedAt",
        headerName: t("settings.archive.finished"),
        width: 120,
        valueFormatter: (value: string | null) => formatUtc(value),
      },
      {
        field: "actions",
        headerName: "",
        width: 170,
        sortable: false,
        renderCell: (params) => (
          <div className={styles.actionsCell}>
            <HollowButton
              text={t("settings.archive.report")}
              onClick={() => setReportJobId(params.row.Id)}
            />
            {(params.row.Status === EMagistratArchiveJobStatus.Failed ||
              params.row.Status === EMagistratArchiveJobStatus.Cancelled) && (
              <HollowButton
                text={t("settings.archive.retry")}
                onClick={() => jobActions.current.retry(params.row)}
              />
            )}
            {params.row.Status === EMagistratArchiveJobStatus.Queued && (
              <HollowButton
                text={t("common.cancel")}
                onClick={() => jobActions.current.cancel(params.row)}
              />
            )}
          </div>
        ),
      },
    ],
    [t, isAdmin],
  );

  const NoRows = () => (
    <div className={styles.empty}>
      <SearchEmptyImg className={styles.emptyImg} />
      <div>{t("emptyStates.noResults")}</div>
    </div>
  );

  if (isStatusLoading) return <Loading />;

  if (!enabled)
    return (
      <div className={styles.wrapper}>
        <div className={styles.hint}>{t("settings.archive.disabled")}</div>
      </div>
    );

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("settings.archive.casesTitle")}</div>
        <div className={styles.hint}>
          {isAdmin ? t("settings.archive.hintAdmin") : t("settings.archive.hint")}
        </div>
        {!isAdmin && !status?.HasMagistratAccount && (
          <div className={styles.warning}>{t("settings.archive.noAccount")}</div>
        )}

        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Input
              label={t("common.search")}
              labelPosition="none"
              placeholder={t("settings.archive.searchPlaceholder")}
              value={search}
              onChange={setSearch}
              size="small"
            />
          </div>
          <Button
            text={t("settings.archive.importSelected", { count: selectedIds.length })}
            disabled={selectedIds.length === 0 || isImporting}
            loading={isImporting}
            onClick={handleImport}
          />
        </div>
        {casesData?.data?.Truncated && (
          <div className={styles.warning}>{t("settings.archive.truncated")}</div>
        )}

        <div className={styles.casesGrid}>
          <DataGridPremium
            sx={gridSx}
            localeText={plGridLocales}
            rows={rows}
            columns={caseColumns}
            loading={isCasesFetching}
            rowGroupingModel={grouping}
            columnVisibilityModel={{ Employee: false, Year: false, Code: false }}
            groupingColDef={{ headerName: t("settings.archive.group"), minWidth: 170, flex: 1 }}
            defaultGroupingExpansionDepth={debouncedSearch ? -1 : isAdmin ? 0 : 1}
            checkboxSelection
            disableRowSelectionOnClick
            isRowSelectable={(params) =>
              params.row?.State === EMagistratArchiveCaseState.Archived
            }
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            columnHeaderHeight={30}
            rowHeight={40}
            hideFooter
            slots={{ noRowsOverlay: NoRows }}
            slotProps={{
              loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
            }}
          />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("settings.archive.jobsTitle")}</div>
        <div className={styles.hint}>{t("settings.archive.jobsHint")}</div>
        <div className={styles.jobsGrid}>
          <DataGridPremium
            sx={gridSx}
            localeText={plGridLocales}
            rows={jobs}
            columns={jobColumns}
            getRowId={(r) => r.Id}
            loading={isJobsLoading}
            disableRowSelectionOnClick
            columnHeaderHeight={30}
            rowHeight={44}
            hideFooter
            slots={{ noRowsOverlay: NoRows }}
          />
        </div>
      </div>

      {reportJobId && (
        <MagistratArchiveReportModal
          jobId={reportJobId}
          onClose={() => setReportJobId(null)}
          onRetry={(job) => handleRetry(job)}
        />
      )}
    </div>
  );
};

export default MagistratArchiveTab;
